import * as React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { useEffect, useState } from "react";
import { Paper, Typography } from "@mui/material";
import { AsynchronousRequisitesProps, ExtResidentData } from "../../../types/common";
import { getResidents } from "../../../api/residentAPI";

interface Props extends AsynchronousRequisitesProps {}

const filter = createFilterOptions<ExtResidentData>();

const AsynchronousRequisites: React.FC<Props> = ({
    onChange,
    label,
    getParam,
    value,
    error,
    callbackResult,
    disabled,
    id,
}) => {
    const [options, setOptions] = useState<Array<ExtResidentData>>();
    const [select, setSelect] = useState<ExtResidentData>();
    const [query, setQuery] = useState("");
    const [load, setLoad] = useState(false);
    const isInn = getParam === "inn";
    const fetchData = () => {
        setLoad(true);
        getResidents(
            id,
            ({ data }) => {
                setOptions(data);
                setLoad(false);
            },
            err => {
                setLoad(false);
            },
            { page: -1, [getParam ?? "name"]: query },
        );
    };

    useEffect(() => {
        if (query) {
            fetchData();
        }
    }, [query]);
    useEffect(() => {
        setQuery(value);
    }, []);

    const valueGet = (newValue: string) => (isInn ? { requisites: { inn: newValue } } : { name: newValue });

    return (
        <Autocomplete
            value={select}
            onChange={(_event, newValue: string | ExtResidentData | null) => {
                if (typeof newValue === "string") {
                    const value = valueGet(newValue);
                    setSelect(value);
                    callbackResult && callbackResult(value);
                    onChange(value);
                } else if (newValue?.inputValue) {
                    const value = valueGet(newValue?.inputValue);
                    setSelect(value);
                    callbackResult && callbackResult(value);
                    onChange(value);
                } else if (newValue) {
                    setSelect(newValue);
                    callbackResult && callbackResult(newValue);
                    onChange(newValue);
                }
            }}
            inputValue={query ?? ""}
            onInputChange={(_event, newInputValue) => {
                setQuery(newInputValue);
            }}
            noOptionsText={"Нет совпадений"}
            getOptionLabel={option => {
                if (typeof option === "string") return option;
                if (option.inputValue) return option.inputValue;
                return (getParam === "inn" ? option.requisites?.inn : option.name) ?? "";
            }}
            options={options || []}
            loading={load}
            freeSolo
            selectOnFocus
            clearOnBlur
            disabled={disabled}
            PaperComponent={props => (
                <Paper {...props}>
                    {options?.length !== 0 && (
                        <Typography sx={{ p: "8px 16px 0", color: " rgba(0, 0, 0, 0.6)" }} fontSize={"12px"}>
                            Совпадающие данные:
                        </Typography>
                    )}
                    {props.children}
                </Paper>
            )}
            filterOptions={(options, params) => {
                const mappedOptions = options.map(el => (typeof el === "string" ? { name: el } : el));

                const filtered = filter(mappedOptions, params);

                const { inputValue } = params;
                // Suggest the creation of a new value
                const isExisting = mappedOptions.some(option =>
                    isInn ? inputValue === option.requisites?.inn : inputValue === option?.name,
                );
                if (inputValue !== "" && !isExisting) {
                    filtered.push({
                        inputValue,
                        ...(isInn
                            ? { requisites: { inn: `Добавить "${inputValue}"` } }
                            : { name: `Добавить "${inputValue}"` }),
                    });
                }

                return filtered;
            }}
            renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                    <li {...props} key={key}>
                        <Typography gutterBottom {...optionProps}>
                            {typeof option !== "string" &&
                                (getParam === "inn"
                                    ? `${option.requisites?.inn} (${option.name ?? "Н/Д"})`
                                    : `${option.name} (${option.requisites?.inn ?? "Н/Д"})`)}
                        </Typography>
                    </li>
                );
            }}
            renderInput={params => (
                <TextField
                    autoComplete="off"
                    {...params}
                    size={"small"}
                    label={label}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {load ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    error={!!error}
                    helperText={error?.message}
                />
            )}
        />
    );
};
export default AsynchronousRequisites;
